/* IM Results Carousel — isolated, scoped */
(() => {
  const ROOT_SELECTOR = '[data-im-results-carousel]';

  function getState(root) {
    if (!root) return null;

    if (!root.__imResultsState) {
      root.__imResultsState = {
        track: root.querySelector(
          '[data-im-results-track]'
        ),
        cards: Array.from(
          root.querySelectorAll(
            '[data-im-results-card]'
          )
        ),
        dotsWrap: root.querySelector(
          '[data-im-results-dots]'
        ),
        dots: Array.from(
          root.querySelectorAll(
            '[data-im-results-dot]'
          )
        ),
        activeIndex: 0,
        raf: null,
        resizeRaf: null,
      };
    }

    return root.__imResultsState;
  }

  function clampIndex(index, count) {
    if (!count) return 0;

    return Math.max(
      0,
      Math.min(
        Number(index) || 0,
        count - 1
      )
    );
  }

  function trackHasOverflow(root) {
    const state = getState(root);

    if (!state || !state.track) {
      return false;
    }

    return (
      state.track.scrollWidth >
      state.track.clientWidth + 2
    );
  }

  function getCardTargetScroll(
    root,
    index
  ) {
    const state = getState(root);

    if (
      !state ||
      !state.track ||
      !state.cards.length
    ) {
      return 0;
    }

    const card =
      state.cards[
        clampIndex(
          index,
          state.cards.length
        )
      ];

    if (!card) return 0;

    const trackRect =
      state.track.getBoundingClientRect();

    const cardRect =
      card.getBoundingClientRect();

    const rawTarget =
      state.track.scrollLeft +
      (
        cardRect.left -
        trackRect.left
      ) -
      (
        trackRect.width -
        cardRect.width
      ) / 2;

    const maxScroll =
      Math.max(
        0,
        state.track.scrollWidth -
        state.track.clientWidth
      );

    return Math.max(
      0,
      Math.min(
        rawTarget,
        maxScroll
      )
    );
  }

  function getDotTargets(root) {
    const state = getState(root);

    if (
      !state ||
      !state.track ||
      !state.cards.length ||
      !trackHasOverflow(root)
    ) {
      return [];
    }

    const isDesktop =
      window.matchMedia(
        '(min-width: 900px)'
      ).matches;

    const targets = [];
    const duplicateTolerance = 2;

    state.cards.forEach(
      (card, cardIndex) => {
        const scrollLeft =
          getCardTargetScroll(
            root,
            cardIndex
          );

        if (!isDesktop) {
          targets.push({
            cardIndex,
            scrollLeft,
          });

          return;
        }

        const previousTarget =
          targets[
            targets.length - 1
          ];

        const isDuplicate =
          previousTarget &&
          Math.abs(
            previousTarget.scrollLeft -
            scrollLeft
          ) <= duplicateTolerance;

        if (!isDuplicate) {
          targets.push({
            cardIndex,
            scrollLeft,
          });
        }
      }
    );

    return targets;
  }

  function getVisibleDots(root) {
    const state = getState(root);

    if (!state) {
      return [];
    }

    return state.dots.filter(
      (dot) => !dot.hidden
    );
  }

  function syncDotsVisibility(root) {
    const state = getState(root);

    if (
      !state ||
      !state.dotsWrap
    ) {
      return;
    }

    const targets =
      getDotTargets(root);

    state.dots.forEach(
      (dot, dotIndex) => {
        const target =
          targets[dotIndex];

        if (!target) {
          dot.hidden = true;

          dot.classList.remove(
            'is-active'
          );

          dot.setAttribute(
            'aria-current',
            'false'
          );

          dot.removeAttribute(
            'data-im-results-dot-scroll'
          );

          return;
        }

        dot.hidden = false;

        dot.setAttribute(
          'data-im-results-dot-index',
          String(target.cardIndex)
        );

        dot.setAttribute(
          'data-im-results-dot-scroll',
          String(target.scrollLeft)
        );

        dot.setAttribute(
          'aria-label',
          `Go to carousel position ${dotIndex + 1}`
        );
      }
    );

    const showDots =
      targets.length > 1;

    state.dotsWrap.hidden =
      !showDots;

    state.dotsWrap.setAttribute(
      'aria-hidden',
      showDots
        ? 'false'
        : 'true'
    );
  }

  function setActiveDot(root, index) {
    const state = getState(root);

    if (!state) return;

    const visibleDots =
      getVisibleDots(root);

    state.activeIndex =
      clampIndex(
        index,
        visibleDots.length
      );

    state.dots.forEach((dot) => {
      const active =
        !dot.hidden &&
        dot ===
          visibleDots[
            state.activeIndex
          ];

      dot.classList.toggle(
        'is-active',
        active
      );

      dot.setAttribute(
        'aria-current',
        active
          ? 'true'
          : 'false'
      );
    });
  }

  function findNearestDotIndex(
    root,
    scrollPosition = null
  ) {
    const state = getState(root);

    if (
      !state ||
      !state.track
    ) {
      return 0;
    }

    const visibleDots =
      getVisibleDots(root);

    if (!visibleDots.length) {
      return 0;
    }

    const currentScroll =
      scrollPosition === null
        ? state.track.scrollLeft
        : Number(scrollPosition) || 0;

    let closestIndex = 0;
    let closestDistance = Infinity;

    visibleDots.forEach(
      (dot, dotIndex) => {
        const targetScroll =
          Number(
            dot.getAttribute(
              'data-im-results-dot-scroll'
            )
          ) || 0;

        const distance =
          Math.abs(
            currentScroll -
            targetScroll
          );

        if (
          distance <
          closestDistance
        ) {
          closestDistance =
            distance;

          closestIndex =
            dotIndex;
        }
      }
    );

    return closestIndex;
  }

  function syncFromScroll(root) {
    const state = getState(root);

    if (
      !state ||
      !state.track
    ) {
      return;
    }

    if (state.raf) {
      cancelAnimationFrame(
        state.raf
      );
    }

    state.raf =
      requestAnimationFrame(() => {
        state.raf = null;

        setActiveDot(
          root,
          findNearestDotIndex(root)
        );
      });
  }

  function scrollToCard(
    root,
    index,
    behavior = 'smooth'
  ) {
    const state = getState(root);

    if (
      !state ||
      !state.track ||
      !state.cards.length
    ) {
      return;
    }

    if (!trackHasOverflow(root)) {
      return;
    }

    const nextIndex =
      clampIndex(
        index,
        state.cards.length
      );

    const targetScroll =
      getCardTargetScroll(
        root,
        nextIndex
      );

    state.track.scrollTo({
      left: targetScroll,
      behavior,
    });

    setActiveDot(
      root,
      findNearestDotIndex(
        root,
        targetScroll
      )
    );
  }

  function scrollToSection(
    targetId
  ) {
    const cleanId =
      String(targetId || '')
        .trim()
        .replace(/^#/, '');

    if (!cleanId) return;

    const target =
      document.getElementById(
        cleanId
      );

    if (!target) {
      console.warn(
        `IM Results Carousel: scroll target #${cleanId} was not found.`
      );

      return;
    }

    const reducedMotion =
      window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;

    target.scrollIntoView({
      behavior:
        reducedMotion
          ? 'auto'
          : 'smooth',
      block: 'start',
    });
  }

  function bindDots(root) {
    const state = getState(root);

    if (!state) return;

    state.dots.forEach((dot) => {
      dot.addEventListener(
        'click',
        () => {
          const index =
            Number(
              dot.getAttribute(
                'data-im-results-dot-index'
              ) || 0
            );

          scrollToCard(
            root,
            index
          );
        }
      );
    });
  }

  function bindScrollCtas(root) {
    root
      .querySelectorAll(
        '[data-im-results-scroll-target]'
      )
      .forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            scrollToSection(
              button.getAttribute(
                'data-im-results-scroll-target'
              )
            );
          }
        );
      });
  }

  function preloadPopupImages(root) {
    if (
      !root ||
      root.__imResultsPopupImagesPreloaded
    ) {
      return;
    }

    root.__imResultsPopupImagesPreloaded =
      true;

    const urls = new Set();

    root
      .querySelectorAll(
        '[data-im-results-popup-src]'
      )
      .forEach((card) => {
        const src =
          String(
            card.getAttribute(
              'data-im-results-popup-src'
            ) || ''
          ).trim();

        if (src) {
          urls.add(src);
        }
      });

    root.__imResultsPopupPreloaders =
      Array.from(urls).map((src) => {
        const image = new Image();

        image.decoding = 'async';
        image.src = src;

        return image;
      });
  }

  function openImageModal(
    root,
    trigger
  ) {
    if (
      !root ||
      !trigger
    ) {
      return;
    }

    const modal =
      root.querySelector(
        '[data-im-results-modal]'
      );

    const modalImage =
      modal
        ? modal.querySelector(
            '[data-im-results-modal-image]'
          )
        : null;

    const closeButton =
      modal
        ? modal.querySelector(
            '.im-results-carousel__modalClose'
          )
        : null;

    const card =
      trigger.closest(
        '[data-im-results-card]'
      );

    if (
      !modal ||
      !modalImage ||
      !card
    ) {
      return;
    }

    const src =
      String(
        card.getAttribute(
          'data-im-results-popup-src'
        ) || ''
      ).trim();

    if (!src) {
      return;
    }

    const alt =
      String(
        card.getAttribute(
          'data-im-results-popup-alt'
        ) || ''
      ).trim();

    if (root.__imResultsModalCloseTimer) {
      window.clearTimeout(
        root.__imResultsModalCloseTimer
      );

      root.__imResultsModalCloseTimer =
        null;
    }

    root.__imResultsLastPopupTrigger =
      trigger;

    modalImage.src = src;
    modalImage.alt = alt;

    modal.hidden = false;

    modal.setAttribute(
      'aria-hidden',
      'false'
    );

    if (
      typeof root.__imResultsPreviousOverflow ===
      'undefined'
    ) {
      root.__imResultsPreviousOverflow =
        document.documentElement.style.overflow;
    }

    document.documentElement.style.overflow =
      'hidden';

    window.requestAnimationFrame(() => {
      modal.classList.add(
        'is-open'
      );

      if (
        closeButton &&
        typeof closeButton.focus === 'function'
      ) {
        closeButton.focus({
          preventScroll: true,
        });
      }
    });
  }

  function closeImageModal(root) {
    if (!root) {
      return;
    }

    const modal =
      root.querySelector(
        '[data-im-results-modal]'
      );

    if (
      !modal ||
      modal.hidden
    ) {
      return;
    }

    modal.classList.remove(
      'is-open'
    );

    modal.setAttribute(
      'aria-hidden',
      'true'
    );

    if (
      typeof root.__imResultsPreviousOverflow !==
      'undefined'
    ) {
      document.documentElement.style.overflow =
        root.__imResultsPreviousOverflow;

      delete root.__imResultsPreviousOverflow;
    }

    if (root.__imResultsModalCloseTimer) {
      window.clearTimeout(
        root.__imResultsModalCloseTimer
      );
    }

    root.__imResultsModalCloseTimer =
      window.setTimeout(() => {
        if (
          !modal.classList.contains(
            'is-open'
          )
        ) {
          modal.hidden = true;
        }

        root.__imResultsModalCloseTimer =
          null;
      }, 180);

    const trigger =
      root.__imResultsLastPopupTrigger;

    if (
      trigger &&
      typeof trigger.focus === 'function'
    ) {
      trigger.focus({
        preventScroll: true,
      });
    }

    root.__imResultsLastPopupTrigger =
      null;
  }

  function bindImagePopup(root) {
    if (
      !root ||
      root.__imResultsPopupBound
    ) {
      return;
    }

    root.__imResultsPopupBound =
      true;

    const modal =
      root.querySelector(
        '[data-im-results-modal]'
      );

    if (!modal) {
      return;
    }

    preloadPopupImages(root);

    root
      .querySelectorAll(
        '[data-im-results-popup-trigger]'
      )
      .forEach((trigger) => {
        trigger.addEventListener(
          'click',
          () => {
            openImageModal(
              root,
              trigger
            );
          }
        );
      });

    modal
      .querySelectorAll(
        '[data-im-results-modal-close]'
      )
      .forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            closeImageModal(root);
          }
        );
      });

    document.addEventListener(
      'keydown',
      (event) => {
        if (
          event.key !== 'Escape' ||
          modal.hidden
        ) {
          return;
        }

        closeImageModal(root);
      }
    );
  }

  function bindTrack(root) {
    const state = getState(root);

    if (
      !state ||
      !state.track
    ) {
      return;
    }

    state.track.addEventListener(
      'scroll',
      () => {
        syncFromScroll(root);
      },
      {
        passive: true,
      }
    );

    window.addEventListener(
      'resize',
      () => {
        if (state.resizeRaf) {
          cancelAnimationFrame(
            state.resizeRaf
          );
        }

        state.resizeRaf =
          requestAnimationFrame(
            () => {
              state.resizeRaf =
                null;

              syncDotsVisibility(
                root
              );

              syncFromScroll(
                root
              );
            }
          );
      },
      {
        passive: true,
      }
    );
  }

  function bindRoot(root) {
    if (
      !root ||
      root.__imResultsBound
    ) {
      return;
    }

    root.__imResultsBound =
      true;

    const state =
      getState(root);

    if (
      !state ||
      !state.cards.length
    ) {
      return;
    }

    bindDots(root);
    bindScrollCtas(root);
    bindImagePopup(root);
    bindTrack(root);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        syncDotsVisibility(
          root
        );

        setActiveDot(
          root,
          findNearestDotIndex(root)
        );
      });
    });
  }

  function init() {
    document
      .querySelectorAll(
        ROOT_SELECTOR
      )
      .forEach(bindRoot);
  }

  document.addEventListener(
    'DOMContentLoaded',
    init
  );

  document.addEventListener(
    'shopify:section:load',
    init
  );

  document.addEventListener(
    'shopify:section:reorder',
    init
  );

  if (
    document.readyState !==
    'loading'
  ) {
    init();
  }
})();